import React, { useState, useEffect, useCallback } from 'react';
import { View, FlatList, ScrollView, StyleSheet, TouchableOpacity, RefreshControl, ActivityIndicator, Platform, TextInput, Keyboard } from 'react-native';
import { RouteProp, useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { LedgerStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import FloatingActionButton from '../../components/common/FloatingActionButton';
import LedgerEntrySkeleton from '../../components/common/LedgerEntrySkeleton';
import AddMemberModal from '../../components/ledger/AddMemberModal';
import * as ledgerApi from '../../lib/api/ledger';
import { storage } from '../../utils/storage';
import { LedgerType } from '../../types/ledger';
import { apiBaseUrl } from '../../config/env';
import { showSuccessToast, showErrorToast, Alert } from '../../utils/toast';

const getTypeColor = (type: LedgerType): string => {
  switch (type) {
    case 'Business': return '#2563eb';
    case 'Personal': return '#16a34a';
    case 'Academics': return '#ca8a04';
    case 'Social': return '#dc2626';
    default: return '#6b7280';
  }
};

type LedgerDetailRouteProp = RouteProp<LedgerStackParamList, 'LedgerDetail'>;
type LedgerDetailNavigationProp = StackNavigationProp<LedgerStackParamList, 'LedgerDetail'>;

interface Transaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  title: string;
  note?: string;
  date: string;
  createdAt: string;
  createdByUserId?: string;
  createdByName?: string;
  participantName?: string;
  participantUserIds?: string[];
}

interface LedgerData {
  id: string;
  userId: string;
  personName: string;
  balance: number;
  totalReceived: number;
  totalPaid: number;
  type?: string;
  note?: string;
  isShared?: boolean;
  linkStatus?: string;
  sharedWithUser?: { id: string; name: string };
  sharedByUser?: { id: string; name: string };
  participants?: Array<{
    id: string;
    userId: string;
    name: string;
    role: 'owner' | 'viewer';
    status: string;
    joinedAt: string;
  }>;
}

const LedgerDetailScreen: React.FC = () => {
  const navigation = useNavigation<LedgerDetailNavigationProp>();
  const route = useRoute<LedgerDetailRouteProp>();
  const { ledgerId, personName } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();

  const [ledgerData, setLedgerData] = useState<LedgerData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'transactions' | 'details'>('transactions');
  const [searchQuery, setSearchQuery] = useState('');
  const [transactionFilter, setTransactionFilter] = useState<'all' | 'received' | 'paid'>('all');
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  // Keyboard listeners
  useEffect(() => {
    const keyboardWillShow = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      () => setKeyboardVisible(true)
    );
    const keyboardWillHide = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => setKeyboardVisible(false)
    );

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, []);

  const loadLedger = async (showLoading = true) => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      if (showLoading) setLoading(true);
      const data = await ledgerApi.getLedger(token, ledgerId);
      console.log('📝 Ledger data received:', {
        id: data.id,
        personName: data.personName,
        note: data.note,
        type: data.type,
        hasNote: !!data.note,
        noteLength: data.note?.length,
        participants: (data as any).participants,
        participantsCount: (data as any).participants?.length
      });
      setLedgerData({
        id: data.id,
        userId: data.userId,
        personName: data.personName,
        balance: parseFloat(data.balance),
        totalReceived: parseFloat(data.totalReceived),
        totalPaid: parseFloat(data.totalPaid),
        type: data.type,
        note: data.note,
        isShared: data.isShared || ((data as any).participants?.length > 1),
        linkStatus: data.linkStatus,
        sharedWithUser: (data as any).sharedWithUser,
        sharedByUser: (data as any).sharedByUser,
        participants: (data as any).participants,
      });
    } catch (error: any) {
      console.error('Error loading ledger:', error);
      Alert.alert('Error', error.message || 'Failed to load ledger');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadTransactions = async (pageNum: number, isRefresh = false) => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else if (pageNum > 1) {
        setLoadingMore(true);
      }

      const type = undefined;
      const result = await ledgerApi.getTransactionsByLedger(token, ledgerId, type, pageNum, 10);
      
      const transactions = result.transactions || [];
      console.log('📊 Transactions received:', transactions.length, transactions[0]);
      if (isRefresh || pageNum === 1) {
        setTransactions(transactions.map(t => ({
          id: t.id,
          type: t.type,
          amount: parseFloat(t.amount),
          title: t.title,
          note: t.note,
          date: t.date,
          createdAt: t.createdAt,
          createdByUserId: t.createdByUserId,
          createdByName: t.createdByName,
          participantName: t.participantName,
          participantUserIds: t.participantUserIds,
        })));
      } else {
        setTransactions(prev => [...prev, ...transactions.map(t => ({
          id: t.id,
          type: t.type,
          amount: parseFloat(t.amount),
          title: t.title,
          note: t.note,
          date: t.date,
          createdAt: t.createdAt,
          createdByUserId: t.createdByUserId,
          createdByName: t.createdByName,
          participantName: t.participantName,
          participantUserIds: t.participantUserIds,
        }))]);
      }
      
      setHasMore(result.hasMore);
      setPage(pageNum);
    } catch (error: any) {
      console.error('Error loading transactions:', error);
      Alert.alert('Error', error.message || 'Failed to load transactions');
    } finally {
      setRefreshing(false);
      setLoadingMore(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLedger();
    loadTransactions(1);
  }, [ledgerId]);

  useFocusEffect(
    useCallback(() => {
      loadLedger(false);
      loadTransactions(1, true);
    }, [ledgerId])
  );

  const handleRefresh = useCallback(() => {
    loadLedger(false);
    loadTransactions(1, true);
  }, [ledgerId]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadTransactions(page + 1);
    }
  };

  const handleAddTransaction = () => {
    navigation.navigate('AddEditLedgerEntry', { ledgerId });
  };

  const handleDownloadExcel = async () => {
    const token = await storage.getToken();
    if (!token) {
      Alert.alert('Error', 'Please log in to download');
      return;
    }

    try {
      const filename = `Ledger_${ledgerData?.personName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      const fileUri = FileSystem.documentDirectory + filename;

      // Download file
      const downloadResult = await FileSystem.downloadAsync(
        `${apiBaseUrl}/api/ledgers/${ledgerId}/export`,
        fileUri,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (downloadResult.status !== 200) {
        throw new Error(`Download failed with status ${downloadResult.status}`);
      }

      // Share/Save the file
      await Sharing.shareAsync(downloadResult.uri, {
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        dialogTitle: 'Save Excel File',
        UTI: 'com.microsoft.excel.xlsx',
      });
      
    } catch (error: any) {
      console.error('Error downloading Excel:', error);
      Alert.alert('Error', error.message || 'Failed to download Excel file');
    }
  };

  const handleEditLedger = () => {
    // Navigate to CreateLedgerAccount screen with edit mode
    navigation.navigate('CreateLedgerAccount' as any, {
      editMode: true,
      ledgerId: ledgerId,
      existingData: {
        personName: ledgerData?.personName || personName,
        type: ledgerData?.type,
        note: ledgerData?.note,
      },
    });
  };

  const handleEditTransaction = (entryId: string) => {
    navigation.navigate('AddEditLedgerEntry', { ledgerId, entryId });
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    const token = await storage.getToken();
    if (!token) return;

    Alert.alert(
      'Delete Transaction',
      'Are you sure you want to delete this transaction?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await ledgerApi.deleteTransaction(token, ledgerId, transactionId);
              showSuccessToast('Transaction deleted successfully');
              handleRefresh();
            } catch (error: any) {
              console.error('Error deleting transaction:', error);
              showErrorToast(error.message || 'Failed to delete transaction');
            }
          },
        },
      ]
    );
  };

  const handleDeleteLedger = async () => {
    const token = await storage.getToken();
    if (!token) return;

    Alert.alert(
      'Delete Ledger',
      `Are you sure you want to permanently delete this ledger with ${ledgerData?.personName}? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await ledgerApi.deleteLedger(token, ledgerId);
              showSuccessToast('Ledger deleted successfully');
              navigation.goBack();
            } catch (error: any) {
              console.error('Error deleting ledger:', error);
              showErrorToast(error.message || 'Failed to delete ledger');
            }
          },
        },
      ]
    );
  };

  const handleLeaveLedger = async () => {
    const token = await storage.getToken();
    if (!token) return;

    Alert.alert(
      'Leave Ledger',
      `Are you sure you want to leave this shared ledger with ${ledgerData?.personName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Leave',
          style: 'destructive',
          onPress: async () => {
            try {
              await ledgerApi.leaveLedger(token, ledgerId);
              showSuccessToast('Left ledger successfully');
              navigation.goBack();
            } catch (error: any) {
              console.error('Error leaving ledger:', error);
              showErrorToast(error.message || 'Failed to leave ledger');
            }
          },
        },
      ]
    );
  };

  const handleRemoveParticipant = async (participantUserId: string, participantName: string) => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      await ledgerApi.removeParticipant(token, ledgerId, participantUserId);
      showSuccessToast(`Removed ${participantName} from ledger`);
      handleRefresh();
    } catch (error: any) {
      console.error('Error removing participant:', error);
      showErrorToast(error.message || 'Failed to remove participant');
    }
  };

  const handleAddMembers = async (userIds: string[]) => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      await ledgerApi.shareLedger(token, ledgerId, userIds);
      showSuccessToast(`Added ${userIds.length} member${userIds.length > 1 ? 's' : ''} to ledger`);
      await handleRefresh();
      console.log('🔄 After refresh, participants:', ledgerData?.participants);
    } catch (error: any) {
      console.error('Error adding members:', error);
      showErrorToast(error.message || 'Failed to add members');
    }
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Check if current user is the owner
  const isOwner = ledgerData && user && ledgerData.userId === user.userId;
  
  // Check if there are any details to show
  const hasSharedInfo = (ledgerData?.participants && ledgerData.participants.length > 1) ||
                        ((isOwner && ledgerData?.sharedWithUser) || (!isOwner && ledgerData?.sharedByUser));
  const hasDetails = ledgerData?.type || hasSharedInfo || ledgerData?.note;

  const renderHeader = (showSearch: boolean) => (
    <View style={styles.headerContainer}>
      {/* Balance Card */}
      <View style={[styles.balanceCard, { backgroundColor: theme.card }]}>
        <View style={styles.balanceHeader}>
          <ThemedText style={[styles.balanceTitle, { color: theme.textSecondary }]}>
            Net Balance
          </ThemedText>
          <ThemedText style={[
            styles.balanceMainAmount,
            { color: (ledgerData?.balance || 0) >= 0 ? '#16a34a' : '#e74c3c' }
          ]}>
            {formatAmount(ledgerData?.balance || 0)}
          </ThemedText>
        </View>
        
        {/* Stats Row */}
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <View style={[styles.statDot, { backgroundColor: '#16a34a' }]} />
            <View>
              <ThemedText style={[styles.statLabel, { color: theme.textTertiary }]}>
                Received
              </ThemedText>
              <ThemedText style={[styles.statValue, { color: theme.text }]}>
                {formatAmount(ledgerData?.totalReceived || 0)}
              </ThemedText>
            </View>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <View style={[styles.statDot, { backgroundColor: '#e74c3c' }]} />
            <View>
              <ThemedText style={[styles.statLabel, { color: theme.textTertiary }]}>
                Paid
              </ThemedText>
              <ThemedText style={[styles.statValue, { color: theme.text }]}>
                {formatAmount(ledgerData?.totalPaid || 0)}
              </ThemedText>
            </View>
          </View>
        </View>
      </View>

      {/* Transaction/Details Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[
            styles.tabButton,
            { borderColor: theme.border },
            activeTab === 'transactions' && { backgroundColor: theme.accent, borderColor: theme.accent }
          ]}
          onPress={() => setActiveTab('transactions')}
        >
          <ThemedText style={[
            styles.tabText,
            { color: theme.text },
            activeTab === 'transactions' && { color: '#fff' }
          ]}>
            Transactions
          </ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.tabButton,
            { borderColor: theme.border },
            activeTab === 'details' && { backgroundColor: theme.accent, borderColor: theme.accent }
          ]}
          onPress={() => setActiveTab('details')}
        >
          <ThemedText style={[
            styles.tabText,
            { color: theme.text },
            activeTab === 'details' && { color: '#fff' }
          ]}>
            Details
          </ThemedText>
        </TouchableOpacity>
      </View>

      {/* Search and Filters (Transactions only) */}
      {showSearch && (
        <View>
          <View style={styles.searchHeaderRow}>
            <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Transactions</ThemedText>
          </View>
          <View style={[styles.searchBar, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}>
            <Ionicons name="search-outline" size={20} color={theme.textTertiary} />
            <TextInput
              style={[styles.searchInput, { color: theme.text }]}
              placeholder="Search by note, amount, or date"
              placeholderTextColor={theme.textTertiary}
              value={searchQuery}
              onChangeText={setSearchQuery}
              returnKeyType="search"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name="close-circle" size={20} color={theme.textSecondary} />
              </TouchableOpacity>
            )}
          </View>
          <View style={[styles.filterContainer, { backgroundColor: theme.background }]}>
            <TouchableOpacity
              style={[
                styles.filterButton,
                { 
                  backgroundColor: transactionFilter === 'all' ? theme.accent : theme.backgroundSecondary,
                  borderColor: transactionFilter === 'all' ? theme.accent : theme.border,
                }
              ]}
              onPress={() => setTransactionFilter('all')}
              activeOpacity={0.7}
            >
              <ThemedText style={[
                styles.filterButtonText,
                { color: transactionFilter === 'all' ? '#fff' : theme.text }
              ]}>
                ALL
              </ThemedText>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[
                styles.filterButton,
                { 
                  backgroundColor: transactionFilter === 'received' ? '#16a34a' : theme.backgroundSecondary,
                  borderColor: transactionFilter === 'received' ? '#16a34a' : theme.border,
                }
              ]}
              onPress={() => setTransactionFilter('received')}
              activeOpacity={0.7}
            >
              <ThemedText style={[
                styles.filterButtonText,
                { color: transactionFilter === 'received' ? '#fff' : theme.text }
              ]}>
                Received
              </ThemedText>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[
                styles.filterButton,
                { 
                  backgroundColor: transactionFilter === 'paid' ? '#e74c3c' : theme.backgroundSecondary,
                  borderColor: transactionFilter === 'paid' ? '#e74c3c' : theme.border,
                }
              ]}
              onPress={() => setTransactionFilter('paid')}
              activeOpacity={0.7}
            >
              <ThemedText style={[
                styles.filterButtonText,
                { color: transactionFilter === 'paid' ? '#fff' : theme.text }
              ]}>
                Paid
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );

  const renderTransaction = ({ item }: { item: Transaction }) => {
    // Check if ledger is shared using participants array
    const isSharedLedger = (ledgerData?.participants && ledgerData.participants.length > 1) ||
                           (ledgerData?.isShared && ledgerData?.linkStatus === 'accepted');
    const canEdit = user && item.createdByUserId === user.userId;
    
    // Backend already handles reversal - just use the type as-is
    const displayType = item.type;
    
    // Find participant name if present
    let transactionPerson = '';
    if (item.participantName) {
      transactionPerson = item.participantName;
    } else if (item.participantUserIds && item.participantUserIds.length > 0 && ledgerData?.participants) {
      const found = ledgerData.participants.find(p => p.userId === item.participantUserIds![0]);
      transactionPerson = found?.name || '';
    }

    return (
      <TouchableOpacity
        style={[styles.transactionCard, { backgroundColor: theme.backgroundSecondary }]}
        onPress={canEdit ? () => handleEditTransaction(item.id) : undefined}
        onLongPress={canEdit ? () => handleDeleteTransaction(item.id) : undefined}
        disabled={!canEdit}
      >
        <View style={styles.transactionHeader}>
          <View style={styles.transactionMain}>
            <View style={[
              styles.typeIndicator,
              { backgroundColor: displayType === 'credit' ? '#16a34a' : '#e74c3c' }
            ]} />
            <View style={styles.transactionInfo}>
              <ThemedText style={[styles.transactionDescription, { color: theme.text }]}> 
                {(item.title && item.title.trim()) || item.note || 'No title'}
              </ThemedText>
              {/* Always show note when provided */}
              {item.note ? (
                <ThemedText style={[styles.transactionNote, { color: theme.textSecondary }]} numberOfLines={2}> 
                  {item.note}
                </ThemedText>
              ) : null}
              {/* Show transaction person name if available */}
              {transactionPerson ? (
                <ThemedText style={[styles.transactionPerson, { color: theme.textTertiary }]}> 
                  With: {transactionPerson}
                </ThemedText>
              ) : null}
              <View style={styles.transactionMeta}>
                <ThemedText style={[styles.transactionDate, { color: theme.textSecondary }]}> 
                  {formatDate(item.date)}
                </ThemedText>
                {isSharedLedger && item.createdByName && (
                  <>
                    <ThemedText style={[styles.metaSeparator, { color: theme.textTertiary }]}> 
                      {' • '}
                    </ThemedText>
                    <ThemedText style={[styles.addedBy, { color: theme.textTertiary }]}> 
                      Added by {item.createdByName}
                    </ThemedText>
                  </>
                )}
              </View>
            </View>
          </View>
          <View style={styles.transactionRight}>
            <ThemedText style={[
              styles.transactionAmount,
              { color: displayType === 'credit' ? '#16a34a' : '#e74c3c' }
            ]}>
              {displayType === 'credit' ? '+' : '-'} {formatAmount(item.amount)}
            </ThemedText>
            <Ionicons name="chevron-forward" size={16} color={theme.textTertiary} />
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="receipt-outline" size={64} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
        No transactions yet
      </ThemedText>
      <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
        {isOwner ? 'Tap the + button to add your first transaction' : 'This ledger has no transactions'}
      </ThemedText>
    </View>
  );

  const renderFooter = () => {
    // Add extra space when keyboard is visible
    if (keyboardVisible) {
      return <View style={{ height: 300 }} />;
    }
    
    if (!hasMore) return null;

    return (
      <TouchableOpacity
        style={[styles.loadMoreButton, { backgroundColor: theme.backgroundSecondary }]}
        onPress={handleLoadMore}
        disabled={loadingMore}
      >
        {loadingMore ? (
          <ActivityIndicator size="small" color={theme.accent} />
        ) : (
          <ThemedText style={[styles.loadMoreText, { color: theme.accent }]}>
            Load More
          </ThemedText>
        )}
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={[styles.headerTitle, { color: theme.text }]}>{personName}</ThemedText>
          <View style={styles.backButton} />
        </View>
        <View style={styles.loadingContainer}>
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
        </View>
      </SafeAreaView>
    );
  }

  // Filter transactions based on search query and type
  const filteredTransactions = transactions.filter(transaction => {
    // Filter by type - map UI terms to backend types
    if (transactionFilter !== 'all') {
      const backendType = transactionFilter === 'received' ? 'credit' : 'debit';
      if (transaction.type !== backendType) {
        return false;
      }
    }
    
    // Filter by search query
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const note = (transaction.note || '').toLowerCase();
    const amount = transaction.amount.toString();
    const date = formatDate(transaction.date).toLowerCase();
    return note.includes(query) || amount.includes(query) || date.includes(query);
  });

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]} numberOfLines={1}>
          {personName}
        </ThemedText>
        <View style={styles.headerActions}>
          <TouchableOpacity onPress={handleDownloadExcel} style={styles.headerActionButton}>
            <Ionicons name="download-outline" size={24} color={theme.accent} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleEditLedger} style={styles.headerActionButton}>
            <Ionicons name="pencil" size={24} color={theme.accent} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Details Tab Content */}
      {activeTab === 'details' && (
        <ScrollView style={styles.detailsScrollView} showsVerticalScrollIndicator={false}>
          {renderHeader(false)}
          {/* Details Section */}
          {hasDetails ? (
            <View style={styles.detailsContent}>
              {/* Type */}
              {ledgerData?.type && (
                <View style={[styles.detailItem, { backgroundColor: theme.card, borderColor: theme.border }]}>
                  <View style={styles.detailItemHeader}>
                    <Ionicons name="pricetag-outline" size={16} color={theme.textSecondary} />
                    <ThemedText style={[styles.detailItemLabel, { color: theme.textSecondary }]}>
                      Type
                    </ThemedText>
                  </View>
                  <View style={[styles.typeBadge, { backgroundColor: getTypeColor(ledgerData.type as LedgerType) + '20' }]}>
                    <ThemedText style={[styles.typeBadgeText, { color: getTypeColor(ledgerData.type as LedgerType) }]}>
                      {ledgerData.type}
                    </ThemedText>
                  </View>
                </View>
              )}
              
              {/* Shared Info - Using Participants */}
              {ledgerData?.participants && ledgerData.participants.length > 1 && (
                <View style={[styles.detailItem, { backgroundColor: theme.card, borderColor: theme.border }]}>
                  <View style={styles.detailItemHeader}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Ionicons 
                        name="people-outline" 
                        size={16} 
                        color={theme.textSecondary} 
                      />
                      <ThemedText style={[styles.detailItemLabel, { color: theme.textSecondary }]}>
                        {isOwner ? "Shared with" : "Participants"}
                      </ThemedText>
                    </View>
                    {isOwner && (
                      <TouchableOpacity
                        onPress={() => setShowAddMemberModal(true)}
                        style={[styles.addMemberButton, { backgroundColor: theme.accent }]}
                        activeOpacity={0.7}
                      >
                        <Ionicons name="person-add" size={14} color="#FFFFFF" />
                        <ThemedText style={styles.addMemberButtonText}>Add</ThemedText>
                      </TouchableOpacity>
                    )}
                  </View>
                  <View style={{ gap: 8, marginTop: 8 }}>
                    {(() => {
                      console.log('👥 Filtering participants:', {
                        total: ledgerData.participants.length,
                        currentUserId: user?.userId,
                        ledgerOwnerId: ledgerData.userId,
                        allParticipants: ledgerData.participants.map(p => ({
                          name: p.name,
                          userId: p.userId,
                          role: p.role,
                          status: p.status
                        }))
                      });
                      const filtered = ledgerData.participants
                        .filter(p => p.userId !== user?.userId && p.userId !== ledgerData.userId);
                      console.log('✅ After filter:', filtered.length, 'participants');
                      return filtered;
                    })()
                      .map((participant, index) => (
                        <View
                          key={participant.id}
                          style={styles.participantRow}
                        >
                          <View
                            style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
                          >
                            <View style={{ flex: 1 }}>
                              <ThemedText style={[styles.detailItemValue, { color: theme.text }]}>
                                {participant.name}
                              </ThemedText>
                              {participant.role === 'owner' && (
                                <ThemedText style={[styles.participantRoleText, { color: theme.textSecondary }]}>
                                  Owner
                                </ThemedText>
                              )}
                            </View>
                            <View style={[styles.statusBadgeSmall, { 
                              backgroundColor: participant.status === 'accepted' ? '#27ae60' : 
                                               participant.status === 'rejected' ? '#e74c3c' : '#f39c12' 
                            }]}>
                              <ThemedText style={styles.statusBadgeSmallText}>
                                {participant.status}
                              </ThemedText>
                            </View>
                          </View>
                          
                          {/* 3-dot menu for owner only */}
                          {isOwner && participant.role !== 'owner' && (
                            <TouchableOpacity
                              onPress={() => {
                                Alert.alert(
                                  'Remove Participant',
                                  `Remove ${participant.name} from this ledger?`,
                                  [
                                    { text: 'Cancel', style: 'cancel' },
                                    {
                                      text: 'Remove',
                                      style: 'destructive',
                                      onPress: () => handleRemoveParticipant(participant.userId, participant.name),
                                    },
                                  ]
                                );
                              }}
                              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                              style={styles.participantMenuButton}
                            >
                              <Ionicons name="ellipsis-vertical" size={18} color={theme.textSecondary} />
                            </TouchableOpacity>
                          )}
                        </View>
                      ))}
                  </View>
                </View>
              )}

              {/* Note */}
              {ledgerData?.note && (
                <View style={[styles.detailItem, { backgroundColor: theme.card, borderColor: theme.border }]}>
                  <View style={styles.detailItemHeader}>
                    <Ionicons name="document-text-outline" size={16} color={theme.textSecondary} />
                    <ThemedText style={[styles.detailItemLabel, { color: theme.textSecondary }]}>
                      Note
                    </ThemedText>
                  </View>
                  <ThemedText style={[styles.detailItemValue, { color: theme.text }]}>
                    {ledgerData.note}
                  </ThemedText>
                </View>
              )}
            </View>
          ) : (
            <View style={styles.emptyDetails}>
              <ThemedText style={[styles.emptyDetailsText, { color: theme.textSecondary }]}>
                No additional details for this ledger
              </ThemedText>
            </View>
          )}

          {/* Delete/Leave Button */}
          <View style={styles.actionButtonContainer}>
            {isOwner ? (
              <TouchableOpacity
                style={[styles.deleteButton, { backgroundColor: '#e74c3c' }]}
                onPress={handleDeleteLedger}
              >
                <Ionicons name="trash-outline" size={20} color="#fff" />
                <ThemedText style={styles.deleteButtonText}>Delete Ledger</ThemedText>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[styles.leaveButton, { backgroundColor: '#f39c12' }]}
                onPress={handleLeaveLedger}
              >
                <Ionicons name="exit-outline" size={20} color="#fff" />
                <ThemedText style={styles.leaveButtonText}>Leave Ledger</ThemedText>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      )}

      {/* Transactions List - Only show for transactions tab */}
      {activeTab === 'transactions' && (
        <FlatList
          data={filteredTransactions}
          renderItem={renderTransaction}
          keyExtractor={item => item.id}
          ListHeaderComponent={() => renderHeader(true)}
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={renderFooter}
          contentContainerStyle={filteredTransactions.length === 0 ? styles.emptyListContent : styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={theme.accent} />
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* Both owner and shared users can add transactions */}
      {activeTab === 'transactions' && (
        <FloatingActionButton
          onPress={handleAddTransaction}
        />
      )}

      {/* Add Member Modal */}
      <AddMemberModal
        visible={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onAddMembers={handleAddMembers}
        existingMemberIds={ledgerData?.participants?.map(p => p.userId) || []}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  downloadButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerActionButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerContainer: {
    marginBottom: 16,
  },
  balanceCard: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 4,
    padding: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
  },
  balanceHeader: {
    marginBottom: 16,
  },
  balanceTitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  balanceMainAmount: {
    fontSize: 36,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    justifyContent: 'center',
  },
  statDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: '500',
  },
  statValue: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 2,
  },
  statDivider: {
    width: 1,
    height: 32,
    marginHorizontal: 8,
  },
  detailsToggle: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  detailsToggleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailsToggleText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  detailsScrollView: {
    flex: 1,
  },
  detailsContent: {
    marginHorizontal: 16,
    marginTop: 8,
    gap: 8,
  },
  emptyDetails: {
    marginHorizontal: 16,
    marginTop: 24,
    marginBottom: 16,
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyDetailsText: {
    fontSize: 14,
    textAlign: 'center',
  },
  detailItem: {
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  detailItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  detailItemLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  detailItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  detailItemValue: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  typeBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  typeBadgeText: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  statusBadgeSmall: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
  },
  statusBadgeSmallText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
    textTransform: 'uppercase',
  },
  infoCardsContainer: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginTop: 12,
    gap: 12,
  },
  infoCard: {
    padding: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  infoCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  infoCardLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  infoCardValue: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 18,
  },
  miniStatusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    marginTop: 8,
  },
  miniStatusText: {
    fontSize: 9,
    fontWeight: '700',
    color: '#fff',
    textTransform: 'uppercase',
  },
  typeBadgeLarge: {
    marginTop: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  typeBadgeLargeText: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  summaryCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: 13,
    marginBottom: 4,
  },
  summaryAmount: {
    fontSize: 18,
    fontWeight: '700',
  },
  balanceContainer: {
    borderTopWidth: 1,
    paddingTop: 16,
    alignItems: 'center',
  },
  balanceLabel: {
    fontSize: 14,
    marginBottom: 4,
  },
  balanceAmount: {
    fontSize: 24,
    fontWeight: '700',
  },
  infoRow: {
    marginHorizontal: 16,
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infoRowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  infoRowLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  infoRowValue: {
    fontSize: 13,
    fontWeight: '600',
  },
  compactStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  compactStatusText: {
    fontSize: 9,
    fontWeight: '700',
    color: '#fff',
    textTransform: 'uppercase',
  },
  noteRowContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    flex: 1,
  },
  noteRowText: {
    fontSize: 12,
    flex: 1,
    lineHeight: 16,
  },
  sharedInfoCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  sharedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  sharedTitle: {
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
    textTransform: 'uppercase',
  },
  sharedUsersContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  sharedUserChip: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  sharedUserName: {
    fontSize: 12,
    fontWeight: '500',
  },
  noteCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  noteTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  noteText: {
    fontSize: 14,
    lineHeight: 20,
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 10,
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 12,
  },
  filterButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
  },
  filterText: {
    fontSize: 13,
    fontWeight: '600',
  },
  filterButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  // Tab styles
  tabContainer: {
    flexDirection: 'row',
    gap: 10,
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 12,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
  },
  // Action button styles
  actionButtonContainer: {
    marginHorizontal: 16,
    marginTop: 24,
    marginBottom: 16,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 8,
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  leaveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 8,
  },
  leaveButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '700',
    marginHorizontal: 16,
    marginBottom: 8,
    marginTop: 4,
  },
  searchHeaderRow: {
    marginHorizontal: 16,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    gap: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    padding: 0,
  },
  listContent: {
    paddingBottom: 100,
  },
  emptyListContent: {
    flexGrow: 1,
  },
  transactionCard: {
    marginHorizontal: 16,
    marginBottom: 10,
    padding: 16,
    borderRadius: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  transactionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  transactionMain: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  typeIndicator: {
    width: 4,
    height: 44,
    borderRadius: 2,
    marginRight: 12,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionDescription: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 4,
  },
  transactionNote: {
    fontSize: 13,
    marginBottom: 4,
    fontStyle: 'italic',
  },
  transactionPerson: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 4,
  },
  transactionMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  transactionDate: {
    fontSize: 13,
  },
  metaSeparator: {
    fontSize: 13,
  },
  addedBy: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  transactionRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  transactionAmount: {
    fontSize: 16,
    fontWeight: '700',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  loadMoreButton: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 16,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  loadMoreText: {
    fontSize: 15,
    fontWeight: '600',
  },
  participantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  participantRoleText: {
    fontSize: 11,
    marginTop: 2,
  },
  participantMenuButton: {
    padding: 8,
    marginLeft: 'auto',
  },
  addMemberButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  addMemberButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

export default LedgerDetailScreen;
